<script>
import BreadcrumbsWrapper from '@components/breadcrumbs/breadcrumbs-wrapper.vue';
import ClientSidebar from '@components/client/client-sidebar.vue';
import Layout from '@layouts/main.vue';
import Sidebar from '@components/sidebar/sidebar.vue';
import UseLargeScreen from '@components/use-large-screen.vue';
import { breakpointsMixin } from '@mixins/breakpoints-mixin.js';
import {
  authComputed,
  trainerComputed,
  trainerMethods,
  modalMethods,
  prospectMethods,
  modalComputed,
  prospectComputed,
} from '@state/helpers';

export default {
  page: {
    title: 'Prospect Detail',
    meta: [{ name: 'description', content: 'The Prospect Detail page.' }],
  },
  props: {
    prospect: {
      type: Object,
      required: true,
      default: () => {},
    },
  },
  data() {
    return {
      modalTimeoutId: null,
    };
  },
  mixins: [breakpointsMixin],
  components: {
    BreadcrumbsWrapper,
    ClientSidebar,
    Layout,
    Sidebar,
    UseLargeScreen,
  },
  computed: {
    ...authComputed,
    ...modalComputed,
    ...prospectComputed,
    ...trainerComputed,
    sidebarType() {
      switch (this.$route.name) {
        case 'prospectProfile':
        case 'prospectMessages':
          return null;
        default:
          return 'light';
      }
    },
    contentBgType() {
      switch (this.$route.name) {
        case 'prospectProfile':
        case 'prospectMessages':
          return '';
        default:
          return 'dark';
      }
    },
    wrapperPadding() {
      switch (this.$route.name) {
        case 'prospectDetail':
          return 'no-pad';
        default:
          return '';
      }
    },
    containerMargin() {
      switch (this.$route.name) {
        case 'prospectDetail':
          return 'no-margin';
        default:
          return '';
      }
    },
  },
  mounted() {
    if (this.notifyModalContext && this.notifyModalContext.retry) {
      this.modalTimeoutId = setTimeout(() => {
        this.$bvModal.show('notify-modal');
      }, 1000);
    }
  },
  destroyed() {
    this.modalTimeoutId = null;
    // Clear the selected prospect if user navigates away from prospectDetail child views
    if (this.$route.matched[0].name !== 'prospectDetail') {
      this.setSelectedProspect(null);
    }

    // Cancel getProspect request to reduce retry handler on potential 502 errors
    if (this.isRequestingProspect) {
      this.setCancelGetProspectRequest();
      this.setIsRequestingProspect(false);
    }
  },
  methods: {
    ...modalMethods,
    ...prospectMethods,
    ...trainerMethods,
  },
};
</script>

<template>
  <UseLargeScreen v-if="isSmallScreenView" />
  <Layout v-else>
    <div :class="['container-wrapper', contentBgType]">
      <Sidebar :type="sidebarType" :collapsible="true">
        <ClientSidebar :prospect="prospect" />
      </Sidebar>

      <div :class="['content-wrapper', wrapperPadding]">
        <div :class="['back-btn-container', containerMargin]">
          <BreadcrumbsWrapper prevPageName="Leads" />
        </div>

        <router-view :prospect="$route.params.prospect"></router-view>
      </div>
    </div>
  </Layout>
</template>

<style lang="scss" scoped>
@import '@design';
// stylelint-disable selector-pseudo-element-no-unknown

.sidebar {
  padding: 3.8rem 0 2rem;
}

.content-wrapper {
  height: 100%;
  padding-bottom: 3rem !important;
  position: relative;

  .back-btn-container {
    align-items: center;
    background: white;
    display: flex;
    justify-content: space-between;
    padding: 1rem 0;
    position: sticky;
    top: -3rem;
    z-index: 9;

    &.no-margin {
      margin: -3.2rem -3.2rem 3.2rem -3.2rem;
      top: -3.2rem;
    }

    .breadcrumb-wrapper {
      margin-bottom: 0;
    }

    .invite-button {
      margin: 1em 0;
      padding: 1rem;
    }

    .icon-back {
      left: -3px;
      margin-right: 0.7rem;
      position: relative;
    }
  }
}

::v-deep .sidebar {
  ul.prospect-onboarding-progress {
    padding: 0 1.2rem;

    li {
      color: black;
      display: flex;
      font-size: 1.4rem;
      letter-spacing: -0.233333px;
      line-height: 2rem;
      margin-bottom: 2rem;
      padding-left: 2.7rem;
      position: relative;

      .router-link-active::after {
        right: -1.2rem;
      }

      .progress-indicator {
        align-items: center;
        border: 1px solid black;
        border-radius: $border-radius-circle;
        box-sizing: border-box;
        display: flex;
        height: 1.7rem;
        justify-content: center;
        left: 0;
        margin-right: 1.2rem;
        position: absolute;
        width: 1.7rem;

        &.complete {
          background-color: black;

          .fa-check {
            color: white;
            font-size: 1rem;
          }
        }
      }
    }
  }

  .sidebar-collapsed {
    ul.prospect-onboarding-progress {
      align-items: center;
      display: flex;
      flex-direction: column;

      li {
        margin-bottom: 4rem;
        padding-left: 1.7rem;
      }

      li span {
        display: none;
      }
    }
  }
}
</style>
